import { Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useCapabilities } from '@/hooks/useCapabilities'
import type { Capability } from '@/hooks/useCapabilities'
import type { UserRole } from '@/types/auth'

interface RequireRoleProps {
  roles: UserRole[]
  /**
   * Capabilities that also admit the caller, whatever their base role.
   *
   * WHY THIS EXISTS. `roles` compares `user.role`, so a CUSTOM ROLE can only
   * present its base role here — and a custom role is additive, so a grant
   * beyond that base is invisible to this guard. For several resources the
   * server was deliberately changed to honour exactly such a grant while the
   * route guard was never updated to match, leaving the capability working
   * through the API and the page unreachable in the console. Trust is the
   * clearest case: routes.go records that `RequirePermission(action, "trust")`
   * replaced a role check that "never called OPA, making a custom role's
   * trust:read/manage grant inert" — the server half of that fix shipped, the
   * console half did not.
   *
   * TWO conditions before adding a capability here, both learned the hard
   * way on this very change:
   *
   *  1. The SERVER must serve that grantee for EVERY request the page makes —
   *     not merely the route-level RequirePermission. compliance:read passes
   *     that gate and is then refused by an in-handler role switch
   *     (evidence_pack_handler.go:65), so admitting it would offer a tab
   *     whose every panel 403s.
   *  2. The destination page's OWN gate must accept them. trust:manage alone
   *     passes nothing: TrustPage gates on trust:read.
   *
   * And only pass keys the server answers for (useCapabilities'
   * SERVER_ANSWERABLE list) — for any other key the value is derived from the
   * base role alone, so it would admit exactly who `roles` already admits
   * while implying a delegation that cannot happen.
   *
   * This widens who may RENDER a page, never what they may do on it: every
   * request the page makes is still authorized server-side, so the worst case
   * from a wrong capability is an empty page, not disclosed data.
   */
  anyCapability?: Capability[]
  children: React.ReactNode
  redirectTo?: string
}

/**
 * Route guard that checks if the current user has one of the required roles,
 * or holds one of `anyCapability`.
 * Silently redirects to the dashboard (not a 403 page) to avoid leaking
 * that the route exists to unauthorized users.
 */
export function RequireRole({
  roles,
  anyCapability,
  children,
  redirectTo = '/tenant/dashboard',
}: RequireRoleProps) {
  const { user, isAuthenticated, hasRole } = useAuth()
  // Called unconditionally — a hook cannot sit behind a branch — and cheap:
  // one memoised derivation per session.
  const caps = useCapabilities()

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  const admittedByCapability = anyCapability?.some((c) => caps[c]) ?? false
  if (!hasRole(roles) && !admittedByCapability) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}
